import { differenceInDays, parseISO } from "date-fns";
import { closeHealthRank } from "@common/types/closeHealth";
import { ClientsWorkOverviewSortValue } from "@common/types/views/clientsWorkOverviewView";
import type { ClientsWorkOverviewWorkRow } from "./clientsWorkOverviewViewConfig";
import { DEFAULT_WORK_SORT } from "./workViewSort";

/** Ascending compare for nullable strings, NULLS LAST. */
function compareNullableStringAsc(
    a: string | null | undefined,
    b: string | null | undefined,
): number {
    if (!a && !b) {
        return 0;
    }
    if (!a) {
        return 1;
    }
    if (!b) {
        return -1;
    }
    return a.localeCompare(b);
}

/** Descending compare for nullable strings, NULLS LAST. */
function compareNullableStringDesc(
    a: string | null | undefined,
    b: string | null | undefined,
): number {
    if (!a && !b) {
        return 0;
    }
    if (!a) {
        return 1;
    }
    if (!b) {
        return -1;
    }
    return b.localeCompare(a);
}

/** Ascending compare for nullable numbers, NULLS LAST. */
function compareNullableNumberAsc(
    a: number | null | undefined,
    b: number | null | undefined,
): number {
    if (a == null && b == null) {
        return 0;
    }
    if (a == null) {
        return 1;
    }
    if (b == null) {
        return -1;
    }
    return a - b;
}

/** Descending compare for nullable numbers, NULLS LAST. */
function compareNullableNumberDesc(
    a: number | null | undefined,
    b: number | null | undefined,
): number {
    if (a == null && b == null) {
        return 0;
    }
    if (a == null) {
        return 1;
    }
    if (b == null) {
        return -1;
    }
    return b - a;
}

/**
 * Whole days elapsed since closeStageSetAt, or null when the value is not
 * shown. Mirrors WorkTimeInStageCell exactly: finished rows and rows with no
 * stage timestamp render empty, so they sort as NULL (last) here. Uses
 * `differenceInDays` so the sort key equals the whole-day number displayed in
 * the cell instead of ordering by fractional days or hidden timestamps.
 */
export function daysInStage(
    row: ClientsWorkOverviewWorkRow,
    now: Date = new Date(),
): number | null {
    if (row.isFinished || !row.closeStageSetAt) {
        return null;
    }
    return differenceInDays(now, parseISO(row.closeStageSetAt));
}

/** Distinct engagement-team member count across preparers, reviewers, and managers. */
function engagementTeamCount(row: ClientsWorkOverviewWorkRow): number {
    const ids = new Set<number>();
    for (const member of [
        ...row.preparers,
        ...row.reviewers,
        ...row.managers,
    ]) {
        ids.add(member.userId);
    }
    return ids.size;
}

/**
 * Total estimated time (remaining + done), or null when the close has no
 * estimated-time tasks. Mirrors the BE `SUM("estimatedTime")` over tasks with
 * `estimatedTime > 0`, which yields NULL (not 0) for a close without any such
 * task, so an empty estimate sorts NULLS LAST in both directions like the
 * sibling numeric columns.
 */
function estimatedTimeTotal(row: ClientsWorkOverviewWorkRow): number | null {
    const total = row.estimatedTimeRemaining + row.estimatedTimeDone;
    return total > 0 ? total : null;
}

/** Completion ratio (terminated / total), or null when there are no tasks (matches BE NULLIF(totalTaskCount, 0)). */
function progressRatio(row: ClientsWorkOverviewWorkRow): number | null {
    if (!row.totalTaskCount) {
        return null;
    }
    return row.terminatedTasksCount / row.totalTaskCount;
}

function compareBySort(
    a: ClientsWorkOverviewWorkRow,
    b: ClientsWorkOverviewWorkRow,
    sort: ClientsWorkOverviewSortValue,
): number {
    switch (sort) {
        case ClientsWorkOverviewSortValue.NAME_ASC:
            return a.workspaceName.localeCompare(b.workspaceName);
        case ClientsWorkOverviewSortValue.NAME_DESC:
            return b.workspaceName.localeCompare(a.workspaceName);
        case ClientsWorkOverviewSortValue.APPLIED_AT_ASC:
            return (a.createdAt ?? "").localeCompare(b.createdAt ?? "");
        case ClientsWorkOverviewSortValue.APPLIED_AT_DESC:
            return (b.createdAt ?? "").localeCompare(a.createdAt ?? "");
        case ClientsWorkOverviewSortValue.WORK_DELIVERY_DATE_ASC:
            return compareNullableStringAsc(
                a.workDeliveryDate,
                b.workDeliveryDate,
            );
        case ClientsWorkOverviewSortValue.WORK_DELIVERY_DATE_DESC:
            return compareNullableStringDesc(
                a.workDeliveryDate,
                b.workDeliveryDate,
            );
        case ClientsWorkOverviewSortValue.SERVICE_NAME_ASC:
            return a.name.localeCompare(b.name);
        case ClientsWorkOverviewSortValue.SERVICE_NAME_DESC:
            return b.name.localeCompare(a.name);
        case ClientsWorkOverviewSortValue.ENTITY_ASC:
            return a.entityName.localeCompare(b.entityName);
        case ClientsWorkOverviewSortValue.ENTITY_DESC:
            return b.entityName.localeCompare(a.entityName);
        case ClientsWorkOverviewSortValue.SERVICE_PERIOD_ASC:
            return compareNullableStringAsc(a.startDate, b.startDate);
        case ClientsWorkOverviewSortValue.SERVICE_PERIOD_DESC:
            return compareNullableStringDesc(a.startDate, b.startDate);
        case ClientsWorkOverviewSortValue.WORK_START_ASC:
            return compareNullableStringAsc(a.workStartDate, b.workStartDate);
        case ClientsWorkOverviewSortValue.WORK_START_DESC:
            return compareNullableStringDesc(a.workStartDate, b.workStartDate);
        case ClientsWorkOverviewSortValue.DAYS_IN_STAGE_ASC:
            return compareNullableNumberAsc(daysInStage(a), daysInStage(b));
        case ClientsWorkOverviewSortValue.DAYS_IN_STAGE_DESC:
            return compareNullableNumberDesc(daysInStage(a), daysInStage(b));
        case ClientsWorkOverviewSortValue.ENGAGEMENT_TEAM_COUNT_ASC:
            return engagementTeamCount(a) - engagementTeamCount(b);
        case ClientsWorkOverviewSortValue.ENGAGEMENT_TEAM_COUNT_DESC:
            return engagementTeamCount(b) - engagementTeamCount(a);
        case ClientsWorkOverviewSortValue.PROGRESS_ASC:
            return compareNullableNumberAsc(progressRatio(a), progressRatio(b));
        case ClientsWorkOverviewSortValue.PROGRESS_DESC:
            return compareNullableNumberDesc(
                progressRatio(a),
                progressRatio(b),
            );
        case ClientsWorkOverviewSortValue.ESTIMATED_TIME_ASC:
            return compareNullableNumberAsc(
                estimatedTimeTotal(a),
                estimatedTimeTotal(b),
            );
        case ClientsWorkOverviewSortValue.ESTIMATED_TIME_DESC:
            return compareNullableNumberDesc(
                estimatedTimeTotal(a),
                estimatedTimeTotal(b),
            );
        case ClientsWorkOverviewSortValue.APPLICATION_TYPE_ASC:
            return a.applicationType.localeCompare(b.applicationType);
        case ClientsWorkOverviewSortValue.APPLICATION_TYPE_DESC:
            return b.applicationType.localeCompare(a.applicationType);
        // No display name is available on the row without an extra members
        // lookup (this comparator has no hooks access), so APPLIED_BY sorts
        // numerically by createdByUserId as a documented best-effort fallback.
        // This can visibly differ from the BE's name-based ordering (a known
        // v1 limitation).
        case ClientsWorkOverviewSortValue.APPLIED_BY_ASC:
            return compareNullableNumberAsc(
                a.createdByUserId,
                b.createdByUserId,
            );
        case ClientsWorkOverviewSortValue.APPLIED_BY_DESC:
            return compareNullableNumberDesc(
                a.createdByUserId,
                b.createdByUserId,
            );
        case ClientsWorkOverviewSortValue.HEALTH_ASC:
            return compareNullableNumberAsc(
                closeHealthRank[a.health],
                closeHealthRank[b.health],
            );
        case ClientsWorkOverviewSortValue.HEALTH_DESC:
            return compareNullableNumberDesc(
                closeHealthRank[a.health],
                closeHealthRank[b.health],
            );
        // unreadTaskCount defaults to 0 on freshly-mapped rows until
        // ClientsWorkPage's separate unread-counts query resolves and patches
        // it in. Comparing the field as-is is still correct once that query
        // resolves, consistent with how the count itself is eventually
        // consistent client-side.
        case ClientsWorkOverviewSortValue.UNREAD_TASKS_ASC:
            return a.unreadTaskCount - b.unreadTaskCount;
        case ClientsWorkOverviewSortValue.UNREAD_TASKS_DESC:
            return b.unreadTaskCount - a.unreadTaskCount;
        default:
            return 0;
    }
}

/** Shared Work tie-breaker: client, entity, service, service period (earliest first), close id. */
function compareWorkRowTies(
    a: ClientsWorkOverviewWorkRow,
    b: ClientsWorkOverviewWorkRow,
): number {
    return (
        a.workspaceName.localeCompare(b.workspaceName) ||
        a.entityName.localeCompare(b.entityName) ||
        a.name.localeCompare(b.name) ||
        compareNullableStringAsc(a.startDate, b.startDate) ||
        a.closeId.localeCompare(b.closeId)
    );
}

/**
 * Orders work rows by the active sort, then the shared tie-breaker, so List
 * and Board show rows in the same stable order. The backend sorts workspaces;
 * rows within a workspace or group are sorted here.
 */
export function compareWorkRows(
    a: ClientsWorkOverviewWorkRow,
    b: ClientsWorkOverviewWorkRow,
    sort: ClientsWorkOverviewSortValue | undefined,
): number {
    return (
        compareBySort(a, b, sort ?? DEFAULT_WORK_SORT) ||
        compareWorkRowTies(a, b)
    );
}
